import { scaled, intermediateValue, createDropShadowFilter, createBlurFilter, adjustProjection } from '../utils';
import { casesColorInterpolator } from '../utils/project-utils';
import * as topojson from 'topojson-client';
import choroplethLegend from '../utils/choropleth-legend';
import * as d3 from 'd3';
import { DEFAULT_CHART_WIDTH } from '../constants';

export default function() {
  // required parameters
  let mapData;
  let data;
  let cityLeaIds;

  // constants
  const padding = [0, 0, 35, 0];
  const countryStrokeValue = 0.5; // between 0 and 1
  const cityScale = 8;
  const valueAccessor = d => dataById.get(d.properties.id);
  const height = scaled(600, width);
  const projection = d3.geoMercator();
  const path = d3.geoPath(projection);
  const chartId = 'ireland-map';

  // variables
  let dataById;

  const chart = function(selection) {
    selection.each(function() {
      const container = d3.select(this);

      dataById = new Map();
      data.forEach(datum => {
        dataById.set(datum.id, parseFloat(datum.P14_100k));
      });
      const leas = topojson.feature(mapData, mapData.objects.leas);
      const counties = topojson.feature(mapData, mapData.objects.counties);
      const cities = topojson.feature(mapData, mapData.objects.cities);
      const cityBoundaries = topojson.feature(mapData, mapData.objects.cityBoundaries);
      const mapSize = [width - (padding[1] + padding[3]), height - (padding[0] + padding[2])];
      const [mapScale, _, mapBounds] = adjustProjection(projection, path, leas, {size: mapSize, origin: [padding[3], padding[0]]});
      const extent = d3.extent(leas.features, valueAccessor);
      const colorScale = d3.scaleSequential(casesColorInterpolator()).domain(extent);
      const countyStrokeColor = colorScale(intermediateValue(countryStrokeValue, extent));
      const cityBoundaryStrokeColor = colorScale(intermediateValue(0.4, extent));

      let timer, transform, zoomGroup;

      container.attr('id', chartId)
        .attr('class', 'chart map');

      const tooltip = container.append('div')
        .attr('class', 'tooltip');

      const zoom = d3.zoom()
        .on('zoom', function(e) {
          zoomGroup.attr('transform', (transform = e.transform))
            .select('.internal-boundary')
            .attr('stroke-width', 1 / e.transform.k)
            .attr('stroke-dasharray', `${2 / e.transform.k} ${2 / e.transform.k}`)
        });

      const svg = container.append('svg')
        .attr('viewBox', `0 0 ${width} ${height}`)
        .attr('preserveAspectRatio', 'xMinYMin meet');
        //.call(zoom)

      zoomGroup = svg.append('g')
        .attr('class', 'contents');

      const mainMap = zoomGroup.append('g')
        .attr('class', 'main-map');

      createDropShadowFilter(svg, 'cityLeasDropShadow', 5, 5, 10, 0.5, 20);
      createBlurFilter(svg, 'blur', 2);

      const createLeas = function(parent, features, path) {
        const g = parent.append('g')
          .attr('class', 'leas');

        g.selectAll('path')
          .data(features)
          .enter()
          .append('path')
          .attr('d', path)
          .attr('class', 'area')
          .attr('fill', d => colorScale(valueAccessor(d)))
          .on('mouseover', function(event, d){
            if (timer) {
              window.clearTimeout(timer);
            }
            const position = d3.pointer(event, container.node());
            tooltip.attr('style', `display:block; left: ${position[0] + 10}px; top: ${position[1]}px`);
            tooltip.node().innerHTML = `
          <div class='wrapper'>
            <div class='location'>
              <div class='lea-name'>${d.properties.lea}</div>
              <div class='county-name'>Co. ${d.properties.county}</div>
            </div>
            <div class='value'>${d3.format(',.0f')(valueAccessor(d))}/100k</div>
          </div>`;
            d3.select(this).attr('fill', d => d3.rgb(colorScale(valueAccessor(d))).darker(0.3).toString());
          })
          .on('mouseout', function() {
            d3.select(this).attr('fill', d => colorScale(valueAccessor(d)));
            timer = window.setTimeout(function() {
              tooltip.attr('style', 'display:none')
            }, 300)
          });
        return g
      };

      const getCityBoundary = function(cityBoundaries, city) {
        const features = cityBoundaries.features;
        for (let i = 0; i < features.length; i++) {
          const feature = features[i];
          if (feature.id === city) {
            return feature;
          }
        }
      };

      // LEAs
      createLeas(mainMap, leas.features, path);

      // counties
      mainMap.append('g')
        .attr('class', 'counties')
        .selectAll('path')
        .data(counties.features)
        .enter()
        .append('path')
        .attr('class', 'internal-boundary')
        .attr('stroke', countyStrokeColor)
        .attr('stroke-dasharray', '2 2')
        .attr('d', path);

      // cities
      const g = mainMap.append('g')
        .attr('class', 'cities')
        .selectAll('g')
        .data(cities.features)
        .enter()
        .append('g')
        .attr('class', 'city')
        .attr('transform', d => { const c = projection(d.geometry.coordinates); return `translate(${c[0]} ${c[1]})`   });

      const radius = 4;
      g.append('circle')
        .attr('r', radius);

      g.append('text')
        .attr('x', radius + 2)
        .attr('y', 4)
        .text(d => d.properties.name)
        .on('mouseover', function() {
          d3.select(this.parentNode)
            .attr('class', 'city is-highlighted')
        })
        .on('mouseout', function() {
          d3.select(this.parentNode)
            .attr('class', 'city')
        })
        .on('click', function(event, d) {
          const city = d.properties.name;
          const leaIds = cityLeaIds[city.toLowerCase()];
          const features = leas.features.filter(feature => leaIds.includes(feature.properties.id));

          const projection = d3.geoMercator();
          const path = d3.geoPath(projection);
          const featureCollection = {type: 'FeatureCollection', features:features};
          const mousePosition = d3.pointer(event, svg.node());
          adjustProjection(projection, path, featureCollection, {center:mousePosition, scale:mapScale * cityScale});

          const g = zoomGroup.append('g')
            .attr('class', 'city-leas-group');

          // background
          g.append('rect')
            .attr('width', width)
            .attr('height', height)
            .attr('class', 'modal-bg')
            .on('click', function() {
              g.remove();
              mainMap.attr('style', "")
            });

          // outline
          const cityBoundary = getCityBoundary(cityBoundaries, city);
          if (cityBoundary != null) {
            g.selectAll('path')
              .data([cityBoundary])
              .enter()
              .append('path')
              .attr('d', path)
              .attr('stroke', cityBoundaryStrokeColor)
              .attr('class', 'city-boundary');
          }

          // LEAs
          createLeas(g, features, path)
            .attr('style', 'filter:url(#cityLeasDropShadow)');

          mainMap.attr('style', 'filter:url(#blur)')
        });

      // legend
      mainMap.call(choroplethLegend()
        .chartId(chartId)
        .svgWidth(width)
        .svgHeight(height)
        .colorScale(colorScale)
        .valueAccessor(valueAccessor)
        .features(leas.features)
        .x(mapBounds[1][0] + 50));
    });
  };

  chart.mapData = function(_) {
    if (!arguments.length) return mapData;
    mapData = _;
    return chart;
  };

  chart.data = function(_) {
    if (!arguments.length) return data;
    data = _;
    return chart;
  };

  chart.cityLeaIds = function(_) {
    if (!arguments.length) return cityLeaIds;
    cityLeaIds = _;
    return chart;
  };

  return chart;
}


